import { DestroyRef } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationSkipped,
  NavigationStart,
  Router,
} from '@angular/router';

/** Chiave namespaced della guardia anti-loop del reload su chunk morto. */
export const CHUNK_LOAD_RELOAD_KEY = 'amsuite-chunk-load-reload-at';
export const CHUNK_LOAD_RELOAD_COOLDOWN_MS = 60_000;

const CHUNK_LOAD_MESSAGE_PATTERNS: readonly RegExp[] = [
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /Loading chunk .+ failed/i,
  /Loading CSS chunk .+ failed/i,
];

export interface ChunkLoadRecoveryHooks {
  storageKey?: string;
  cooldownMs?: number;
  now?: () => number;
  getItem?: (key: string) => string | null;
  setItem?: (key: string, value: string) => void;
  reload?: () => void;
}

function extractFailureText(value: unknown, depth = 0): { name: string; message: string } {
  if (depth > 4 || value == null) {
    return { name: '', message: '' };
  }
  if (typeof value === 'string') {
    return { name: '', message: value };
  }
  if (value instanceof Error) {
    return { name: value.name ?? '', message: value.message ?? '' };
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const nested = record['reason'] ?? record['rejection'] ?? record['error'] ?? record['ngOriginalError'];
    if (nested !== undefined && nested !== value) {
      const fromNested = extractFailureText(nested, depth + 1);
      if (fromNested.name || fromNested.message) {
        return fromNested;
      }
    }
    return {
      name: typeof record['name'] === 'string' ? record['name'] : '',
      message: typeof record['message'] === 'string' ? record['message'] : '',
    };
  }
  return { name: '', message: String(value) };
}

/** True per i fallimenti di import() lazy dopo un deploy che ha cambiato gli hash. */
export function isChunkLoadFailure(error: unknown): boolean {
  const { name, message } = extractFailureText(error);
  if (name === 'ChunkLoadError' || name === 'CSSChunkLoadError') {
    return true;
  }
  return CHUNK_LOAD_MESSAGE_PATTERNS.some(pattern => pattern.test(message) || pattern.test(name));
}

function readStorageItem(key: string, hooks: ChunkLoadRecoveryHooks): string | null {
  if (hooks.getItem) {
    return hooks.getItem(key);
  }
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorageItem(key: string, value: string, hooks: ChunkLoadRecoveryHooks): boolean {
  if (hooks.setItem) {
    hooks.setItem(key, value);
    return true;
  }
  try {
    sessionStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function performReload(hooks: ChunkLoadRecoveryHooks): void {
  if (hooks.reload) {
    hooks.reload();
    return;
  }
  window.location.reload();
}

/**
 * Un solo reload per sessione/cooldown. Senza storage non si puo' garantire
 * l'anti-loop: non ricaricare (l'utente fa F5 a mano).
 */
export function reloadOnceOnChunkFailure(error: unknown, hooks: ChunkLoadRecoveryHooks = {}): boolean {
  if (!isChunkLoadFailure(error)) {
    return false;
  }

  const storageKey = hooks.storageKey ?? CHUNK_LOAD_RELOAD_KEY;
  const cooldownMs = hooks.cooldownMs ?? CHUNK_LOAD_RELOAD_COOLDOWN_MS;
  const now = hooks.now ?? Date.now;

  const stored = readStorageItem(storageKey, hooks);
  if (stored != null && stored !== '') {
    const last = Number(stored);
    if (Number.isFinite(last) && now() - last < cooldownMs) {
      return false;
    }
  }

  if (!writeStorageItem(storageKey, String(now()), hooks)) {
    return false;
  }

  performReload(hooks);
  return true;
}

/** Listener window. Da chiamare in main.ts prima del bootstrap Angular. */
export function installChunkLoadRecovery(hooks: ChunkLoadRecoveryHooks = {}): () => void {
  const onRejection = (event: PromiseRejectionEvent): void => {
    reloadOnceOnChunkFailure(event.reason, hooks);
  };
  const onError = (event: ErrorEvent): void => {
    reloadOnceOnChunkFailure(event.error ?? event.message, hooks);
  };

  window.addEventListener('unhandledrejection', onRejection);
  window.addEventListener('error', onError);
  return () => {
    window.removeEventListener('unhandledrejection', onRejection);
    window.removeEventListener('error', onError);
  };
}

/** Barra di navigazione + recovery su NavigationError da chunk morto. */
export function bindRouteNavigationProgress(
  router: Router,
  isNavigating: { set(value: boolean): void },
  destroyRef?: DestroyRef,
  hooks: ChunkLoadRecoveryHooks = {},
): void {
  const subscription = router.events.subscribe(event => {
    if (event instanceof NavigationStart) {
      isNavigating.set(true);
      return;
    }
    if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationSkipped) {
      isNavigating.set(false);
      return;
    }
    if (event instanceof NavigationError) {
      isNavigating.set(false);
      reloadOnceOnChunkFailure(event.error, hooks);
    }
  });
  destroyRef?.onDestroy(() => subscription.unsubscribe());
}
